/**
 * An icon: one of Vesper's drawings.
 *
 * The drawings live as files, one per glyph, in src/assets/icons/vesper/, so
 * their art is swapped in one place: replace a file and keep its name. A glyph
 * is painted as a mask filled with `currentColor`, so it takes the colour of the
 * text around it whatever colour its file was drawn in — the ink of a gold
 * button, a rail section's state, a toast's tone — and a file can be any image
 * with an alpha channel: an SVG today, a WebP if an artist delivers one.
 *
 *   <Icon glyph={GLYPH.close} />                     decoration: hidden from assistive technology
 *   <Icon glyph={GLYPH.warning} label="Warning" />   means something on its own
 *
 * `glyph` comes from a registry: the interface set is GLYPH, beside this file;
 * the game's own glyphs have theirs in components/game/common, since this
 * layer knows no game vocabulary. Both are built with `glyphs()`, and a plain
 * string will not type-check as a glyph.
 *
 *   size  xs 10px · sm 16px · md 20px · lg 24px
 *
 * xs is a badge's glyph, and takes only a drawing made heavy for it — a
 * HeavyGlyph, as GLYPH.tick and the sections' badges are: at 10px the
 * family's stroke is under a pixel. The props say so, so the compiler holds
 * every call, a glyph or a size chosen at runtime included.
 *
 * A drawing laid on its side (`turned()`, glyphs.ts) is drawn a quarter turn
 * clockwise, facing right: the airline's plane, flying right in the plane mark
 * and in a round mark (common/plane-glyph.ts's PLANE_MARK). The turn is the
 * drawing's own, so what passes a drawing on passes its turn with it.
 */

import type { CSSProperties } from 'react';

import { glyphImage, type Drawing, type HeavyGlyph } from './glyphs';
import styles from './icon.module.css';

const SIZE = {
  xs: styles.xs,
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
} as const;

interface IconCommon {
  /** Names the icon when it means something on its own. Without it the icon is decoration. */
  label?: string;
  className?: string;
}

/** The drawing, from GLYPH or the game's registry, or one laid on its side; at xs, one made heavy for it. */
export type IconProps = IconCommon &
  ({ glyph: HeavyGlyph; size: 'xs' } | { glyph: Drawing; size?: Exclude<keyof typeof SIZE, 'xs'> });

export function Icon({ glyph, size = 'md', label, className = '' }: IconProps) {
  // A drawing laid on its side carries its turn with it.
  const drawn = typeof glyph === 'string' ? { glyph, turned: false } : { glyph: glyph.glyph, turned: true };
  const style = { '--glyph': glyphImage(drawn.glyph) } as CSSProperties;
  return (
    <span
      className={[styles.icon, SIZE[size], drawn.turned ? styles.turned : '', className].filter(Boolean).join(' ')}
      style={style}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}
